import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Bookmark,
  BookmarkCheck,
  MapPin,
  Gauge,
  Briefcase,
  Search,
  Loader2,
  Lock,
  Crown,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/auth-context";
import { useAuth } from "@/lib/auth-context";
import { normalizeUrgency, formatUrgency } from "@/lib/scores";

function ScoreDot({ value, max = 10 }) {
  // Defensive: old DB rows can hold `urgency_score` on 0–100 (legacy mirror).
  const v = max === 10 ? normalizeUrgency(value) : Math.max(0, Math.min(max, Number(value) || 0));
  const high = v / max >= 0.7;
  const display = max === 10 ? formatUrgency(value) : (Number.isInteger(v) ? `${v}` : v.toFixed(1));
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        high ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"
      }`}
    >
      <Gauge className="w-3 h-3" /> {value == null ? "—" : display}/{max}
    </span>
  );
}

function caseCardBorderClass({ is_claimed_by_me, claimedByOther }) {
  if (is_claimed_by_me) return "border-[#C5A059] ring-2 ring-[#C5A059]/30";
  if (claimedByOther) return "border-slate-200 opacity-70";
  return "border-slate-200 hover:border-[#C5A059]";
}

function CaseCard({ c, onToggleSave, onClaim, showPriority }) {
  const claimedByOther = c.is_claimed && !c.is_claimed_by_me;
  const borderClass = caseCardBorderClass({
    is_claimed_by_me: c.is_claimed_by_me,
    claimedByOther,
  });
  return (
    <div
      data-testid={`attorney-case-${c.id}`}
      className={`bg-white border rounded-xl p-5 transition flex flex-col ${borderClass}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#C5A059]">
              {c.case_type || "Legal Matter"}
            </p>
            {showPriority && c.priority_rank && c.priority_rank <= 5 && (
              <span
                data-testid={`priority-rank-${c.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0A192F] text-[#C5A059] text-[10px] font-bold uppercase tracking-wider"
              >
                <Crown className="w-3 h-3" /> Priority #{c.priority_rank}
              </span>
            )}
          </div>
          <h3 className="mt-1 font-heading text-lg font-semibold text-[#0A192F] leading-snug">
            {c.attorney_specialty || "Attorney needed"}
          </h3>
        </div>
        <button
          data-testid={`save-${c.id}-btn`}
          onClick={() => onToggleSave(c)}
          className="text-[#C5A059] shrink-0"
          aria-label="Save case"
        >
          {c.is_saved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
        </button>
      </div>
      <p className="mt-3 text-sm text-slate-600 line-clamp-3 leading-relaxed">
        {c.summary?.executive_summary || "Summary pending."}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        {(c.state || c.city) && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
            <MapPin className="w-3 h-3" /> {[c.city, c.state].filter(Boolean).join(", ")}
          </span>
        )}
        <ScoreDot value={c.urgency_score} max={10} />
        {c.case_value_score && (
          <span
            data-testid={`value-${c.id}`}
            className={`px-2 py-0.5 rounded-full font-semibold ${
              c.case_value_score === "high"
                ? "bg-[#C5A059]/20 text-[#0A192F]"
                : c.case_value_score === "medium"
                  ? "bg-amber-50 text-amber-800"
                  : "bg-slate-100 text-slate-700"
            }`}
          >
            {c.case_value_score} value
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
          {c.completeness_score ?? "—"}/100 complete
        </span>
      </div>

      {claimedByOther ? (
        <div className="mt-5 text-sm text-slate-500 italic text-center py-2 bg-slate-50 rounded-lg">
          Claimed by another attorney
        </div>
      ) : c.is_claimed_by_me ? (
        <Link to={`/attorney/cases/${c.id}`} className="mt-5">
          <Button
            data-testid={`open-claimed-${c.id}-btn`}
            className="w-full bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-semibold rounded-lg"
          >
            <BookmarkCheck className="w-4 h-4 mr-2" /> Open your case
          </Button>
        </Link>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-2">
          <Link to={`/attorney/cases/${c.id}`}>
            <Button
              data-testid={`view-${c.id}-btn`}
              variant="outline"
              className="w-full rounded-lg border-slate-300 text-[#0A192F]"
            >
              Review
            </Button>
          </Link>
          <Button
            data-testid={`claim-${c.id}-btn`}
            onClick={() => onClaim(c)}
            className="w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
          >
            Claim
          </Button>
        </div>
      )}
    </div>
  );
}

const CASE_TYPES = [
  "Personal Injury", "Auto Accidents", "Employment", "Family Law", "Divorce",
  "Child Custody", "Immigration", "Criminal Defense", "Contracts", "Construction",
  "Business Litigation", "Probate", "Estate Matters", "Landlord-Tenant",
  "Real Estate", "Workers Compensation", "Medical Malpractice", "Consumer Protection",
  "Civil Litigation",
];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function AttorneyDashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = ["saved", "claimed"].includes(params.get("tab"))
    ? params.get("tab")
    : "all";
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState({
    tier: "starter",
    advanced_filters: false,
    priority_boost: false,
  });
  const [filters, setFilters] = useState({
    case_type: "",
    state: "",
    min_urgency: "",
    case_value: "",
  });
  const [search, setSearch] = useState("");
  const [trialing, setTrialing] = useState(false);
  // Trial eligibility — admins set trial_used=true after a successful trial.
  const trialEligible = user && !user.trial_used && features.tier === "starter";

  // Load tier feature flags once. UI hides gated controls based on these,
  // but the backend independently enforces tier permissions on every request.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/attorney/features");
        if (!cancelled) setFeatures(data);
      } catch (e) {
        // Non-fatal: fall back to the most restrictive (starter) defaults so
        // gated UI stays hidden. Surface the failure for debugging but don't
        // toast it — the dashboard remains usable.
        if (process.env.NODE_ENV !== "production") {
          console.warn("Could not load /attorney/features; defaulting to starter tier", e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCases = async () => {
    setLoading(true);
    try {
      if (tab === "saved") {
        const { data } = await api.get("/attorney/saved");
        setCases(data);
      } else if (tab === "claimed") {
        const { data } = await api.get("/attorney/claimed");
        setCases(data);
      } else {
        const q = {};
        if (filters.case_type) q.case_type = filters.case_type;
        if (filters.state) q.state = filters.state;
        // Only send advanced filters if the user's tier allows them. The
        // backend ignores them anyway for Starter, but this keeps the URL
        // clean and avoids confusing telemetry.
        if (features.advanced_filters) {
          if (filters.min_urgency) q.min_urgency = filters.min_urgency;
          if (filters.case_value) q.case_value = filters.case_value;
        }
        const { data } = await api.get("/attorney/cases", { params: q });
        setCases(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
    // fetchCases is intentionally not in deps — it would change on every
    // render and re-fetch in a loop. The dep list below covers every input
    // it actually reads.
  }, [tab, filters.case_type, filters.state, filters.min_urgency, filters.case_value, features.advanced_filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleSave = async (c) => {
    try {
      if (c.is_saved) {
        await api.delete(`/attorney/cases/${c.id}/save`);
      } else {
        await api.post(`/attorney/cases/${c.id}/save`);
      }
      setCases((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, is_saved: !x.is_saved } : x))
      );
      toast.success(c.is_saved ? "Removed from saved" : "Saved");
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const handleClaim = async (c) => {
    try {
      await api.post(`/attorney/cases/${c.id}/claim`);
      toast.success("Case claimed — client contact info unlocked");
      setCases((prev) =>
        prev.map((x) =>
          x.id === c.id
            ? { ...x, is_claimed: true, is_claimed_by_me: true }
            : x
        )
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not claim case"
      );
      if (e?.response?.status === 409) {
        setCases((prev) =>
          prev.map((x) =>
            x.id === c.id ? { ...x, is_claimed: true } : x
          )
        );
      }
    }
  };

  const handleStartTrial = async () => {
    setTrialing(true);
    try {
      const { data } = await api.post("/billing/start-trial");
      toast.success(`Pro trial activated — 14 days of full access unlocked!`);
      // Refresh user record (picks up trial_used + new plan) and tier flags.
      await refreshUser();
      const { data: f } = await api.get("/attorney/features");
      setFeatures(f);
      // Re-fetch cases so Pro-tier filters and ranking are reflected.
      // Done implicitly via the features.advanced_filters dep on the
      // fetchCases useEffect below; nothing more to do here.
      void data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setTrialing(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return cases;
    const s = search.toLowerCase();
    return cases.filter(
      (c) =>
        c.case_type?.toLowerCase().includes(s) ||
        c.attorney_specialty?.toLowerCase().includes(s) ||
        c.summary?.executive_summary?.toLowerCase().includes(s) ||
        c.state?.toLowerCase().includes(s) ||
        c.city?.toLowerCase().includes(s)
    );
  }, [cases, search]);

  return (
    <div data-testid="attorney-dashboard" className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
            Matched Cases
            {features.tier && (
              <span
                data-testid="tier-badge"
                className={`ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  features.tier === "enterprise"
                    ? "bg-[#0A192F] text-[#C5A059]"
                    : features.tier === "professional"
                      ? "bg-[#C5A059]/20 text-[#0A192F]"
                      : "bg-slate-100 text-slate-700"
                }`}
              >
                {features.priority_boost && <Crown className="w-3 h-3" />}
                {features.tier}
              </span>
            )}
          </p>
          <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F]">
            {tab === "saved" ? "Your saved cases" : "Cases in your scope"}
          </h1>
          <p className="mt-2 text-slate-600">
            Only cases matching your licensed states and practice areas are shown.
            {features.priority_boost && tab === "all" && (
              <span className="ml-1 font-medium text-[#0A192F]">
                Enterprise priority ranking is active.
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="tab-all"
            onClick={() => setParams({})}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "all"
                ? "bg-[#0A192F] text-white"
                : "bg-white border border-slate-200 text-slate-700"
            }`}
          >
            All
          </button>
          <button
            data-testid="tab-saved"
            onClick={() => setParams({ tab: "saved" })}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "saved"
                ? "bg-[#0A192F] text-white"
                : "bg-white border border-slate-200 text-slate-700"
            }`}
          >
            Saved
          </button>
          <button
            data-testid="tab-claimed"
            onClick={() => setParams({ tab: "claimed" })}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "claimed"
                ? "bg-[#0A192F] text-white"
                : "bg-white border border-slate-200 text-slate-700"
            }`}
          >
            Claimed
          </button>
        </div>
      </div>

      <div className={`mt-8 grid sm:grid-cols-2 ${features.advanced_filters ? "lg:grid-cols-5" : "lg:grid-cols-3"} gap-3`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="attorney-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cases…"
            className="pl-10 rounded-lg border-slate-300"
          />
        </div>
        <Select
          value={filters.case_type || "any"}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, case_type: v === "any" ? "" : v }))
          }
        >
          <SelectTrigger data-testid="filter-case-type" className="rounded-lg border-slate-300">
            <SelectValue placeholder="Case type" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="any">All case types</SelectItem>
            {CASE_TYPES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.state || "any"}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, state: v === "any" ? "" : v }))
          }
        >
          <SelectTrigger data-testid="filter-state" className="rounded-lg border-slate-300">
            <SelectValue placeholder="State" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="any">All states</SelectItem>
            {US_STATES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {features.advanced_filters && (
          <>
            <Select
              value={filters.min_urgency || "any"}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, min_urgency: v === "any" ? "" : v }))
              }
            >
              <SelectTrigger data-testid="filter-urgency" className="rounded-lg border-slate-300">
                <SelectValue placeholder="Min urgency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any urgency</SelectItem>
                <SelectItem value="5">5+</SelectItem>
                <SelectItem value="7">7+ (high)</SelectItem>
                <SelectItem value="9">9+ (critical)</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.case_value || "any"}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, case_value: v === "any" ? "" : v }))
              }
            >
              <SelectTrigger data-testid="filter-value" className="rounded-lg border-slate-300">
                <SelectValue placeholder="Case value" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any value</SelectItem>
                <SelectItem value="high">High value</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {!features.advanced_filters && tab === "all" && (
        <div
          data-testid="starter-upgrade-card"
          className="mt-4 p-4 rounded-xl border border-dashed border-[#C5A059]/60 bg-gradient-to-r from-[#FFF8E7] to-white flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#C5A059] mt-0.5 shrink-0" />
            <div>
              <p className="font-heading font-semibold text-[#0A192F]">
                {trialEligible
                  ? "Try Professional free for 14 days"
                  : "Upgrade to unlock advanced filtering"}
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                {trialEligible
                  ? "Unlock urgency, AI score and radius filters instantly. No credit card required. Reverts to Starter automatically when the trial ends."
                  : "Urgency, AI score and radius filters are available on Professional and Enterprise plans. Enterprise also includes priority case ranking."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {trialEligible && (
              <Button
                data-testid="start-trial-btn"
                onClick={handleStartTrial}
                disabled={trialing}
                className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-semibold rounded-lg whitespace-nowrap disabled:opacity-60"
              >
                {trialing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Start 14-day Pro trial
              </Button>
            )}
            <Link to="/billing">
              <Button
                data-testid="upgrade-from-filters-btn"
                variant={trialEligible ? "outline" : "default"}
                className={
                  trialEligible
                    ? "border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-lg whitespace-nowrap"
                    : "bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg whitespace-nowrap"
                }
              >
                {!trialEligible && <Sparkles className="w-4 h-4 mr-2" />}
                {trialEligible ? "See plans" : "Upgrade plan"}
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300"
          >
            <Briefcase className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-4 font-heading text-lg font-semibold text-[#0A192F]">
              No matching cases right now
            </p>
            <p className="mt-1 text-sm text-slate-500">
              We&apos;ll show new cases as they come in. Try widening your
              filters above.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <CaseCard
                key={c.id}
                c={c}
                onToggleSave={handleToggleSave}
                onClaim={handleClaim}
                showPriority={features.priority_boost}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
